import { inject, Injector } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, Observable } from 'rxjs';
import { AuthService } from '../../pages/auth-pages/services/auth.service';
import { ToastService } from '../services/toast.service';
import { sanitizeApiErrorBody } from '../utils/api-error.util';

/** Header interno para marcar una petición ya reintentada tras refrescar (evita loops). */
const RETRY_HEADER = 'X-Auth-Retry';

/** Anti-spam del aviso "sin conexión" cuando fallan varias peticiones a la vez. */
const OFFLINE_TOAST_COOLDOWN_MS = 10_000;
let lastOfflineToastAt = 0;

/**
 * Ruta pública del widget embebible (`#/chat-widget/:businessId/:identifier`), que se
 * renderiza DENTRO de un iframe en la web de un cliente. Ahí no hay sesión del CRM: un
 * 401 no debe redirigir a /signin (el visitante vería el login dentro del widget).
 */
const isEmbeddedChatWidget = (): boolean =>
  typeof window !== 'undefined' && window.location.href.includes('#/chat-widget/');

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const token = localStorage.getItem('auth_token');
  const router = inject(Router);
  const toast = inject(ToastService);

  // AuthService se resuelve PEREZOSAMENTE (solo en las ramas de error), no con
  // `inject(AuthService)` aquí arriba.
  //
  // Motivo: la PRIMERA petición de la app la lanza el propio constructor de
  // AuthService (checkExistingSession -> initTimezone + permissionService.load()).
  // Si el interceptor pide AuthService en ese momento, Angular está a mitad de
  // construirlo y lanza `NG0200: Circular dependency detected for AuthService`.
  // Ese error viajaba por el pipe de la petición y lo tragaba el `catchError` de
  // PermissionService.load(), así que tras cada F5 la matriz de permisos NO se
  // cargaba: `*appHasPermission` ocultaba todos los botones y el sidebar dejaba
  // de filtrar por módulo. Con `injector.get(...)` diferido, la construcción de
  // AuthService ya ha terminado cuando hace falta.
  const injector = inject(Injector);
  const getAuthService = () => injector.get(AuthService);

  const clonedReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(clonedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Saneamiento único para toda la app: si el backend mandó un código técnico
      // (`WORKFLOW_ENROLLMENT_INVALID`, `int_id_bussines`, una traza de la JVM…) en
      // `message`, se vacía aquí para que el `err.error?.message || 'texto por
      // defecto'` de cada componente muestre el texto en español. El código sigue
      // intacto en `err.error.code` y el original en `err.error.technicalMessage`.
      sanitizeApiErrorBody(error.error);

      const hasToken = !!token;
      // Las rutas /api/auth/* (login, refresh, logout) no se reintentan con refresh:
      // un 401 ahí es una credencial inválida real.
      const isAuthEndpoint = clonedReq.url.includes('/api/auth/');
      // ...pero NO todas significan "la sesión murió". Solo el refresh lo significa.
      // Un 401 de /api/auth/login (contraseña mala, empresa inactiva) o de
      // /api/auth/forgot-password es un error de VALIDACIÓN de credenciales: si el
      // navegador ya tenía una sesión abierta en otra pestaña, cerrarla sería un bug
      // (un intento de login fallido echaba al usuario que ya estaba trabajando).
      const isSessionEndpoint = clonedReq.url.includes('/api/auth/refresh');
      const alreadyRetried = clonedReq.headers.has(RETRY_HEADER);
      // En el widget embebido nunca se cierra sesión ni se redirige: el error se propaga
      // tal cual al componente del runtime, que ya muestra su propio mensaje.
      const embeddedWidget = isEmbeddedChatWidget();

      // 401 con token en una petición normal → intentar UN refresh y reintentar.
      if (error.status === 401 && hasToken && !isAuthEndpoint && !alreadyRetried && !embeddedWidget) {
        const authService = getAuthService();
        return authService.refreshAccessToken().pipe(
          // OJO: el catchError va ANTES del switchMap para que SOLO capture el
          // fallo del propio refresh (sesión realmente muerta → cerrar sesión).
          // Si el REINTENTO vuelve a fallar con 401 (p.ej. un 401 de validación de
          // negocio como "Contraseña incorrecta." al eliminar), ese error se propaga
          // al componente SIN cerrar sesión: el token es válido, no es expiración.
          catchError(() => {
            authService.logout();
            router.navigate(['/signin'], { queryParams: { reason: 'sesion-expirada' } });
            return throwError(() => error);
          }),
          switchMap((newToken: string) => {
            const retried = clonedReq.clone({
              setHeaders: { Authorization: `Bearer ${newToken}`, [RETRY_HEADER]: 'true' }
            });
            return next(retried) as Observable<HttpEvent<unknown>>;
          })
        );
      }

      // INC 17.4 — `status === 0` NO cierra sesión. El navegador reporta 0 para un
      // corte de red, un backend reiniciándose o un fallo de CORS: el token sigue
      // siendo válido. Se avisa con un toast y el error se propaga al componente.
      if (error.status === 0 && !isAuthEndpoint) {
        const now = Date.now();
        if (!embeddedWidget && now - lastOfflineToastAt > OFFLINE_TOAST_COOLDOWN_MS) {
          lastOfflineToastAt = now;
          toast.error({ title: 'Sin conexión con el servidor', message: 'No se pudo contactar el servidor. Revisa tu conexión e inténtalo de nuevo.' });
        }
        return throwError(() => error);
      }

      // 401 sin posibilidad de refresh (el propio refresh falló, o la petición ya se
      // reintentó) → la sesión está realmente muerta: cerrar y avisar el motivo.
      // OJO: los 401 de login/forgot/reset NO entran aquí (ver isSessionEndpoint).
      const isUnauthorized = error.status === 401 && (isSessionEndpoint || alreadyRetried);

      if (isUnauthorized && hasToken && !embeddedWidget) {
        getAuthService().logout();
        router.navigate(['/signin'], { queryParams: { reason: 'sesion-expirada' } });
      }
      return throwError(() => error);
    })
  );
};
